import type { FC, RefObject } from "react";

import { useLocatorTranslations } from "@locators/components/shared/hooks/useLocatorTranslations";
import { useStoreLocatorContext } from "@locators/components/shared/hooks/useStoreLocatorContext";
import { useStoreTrackingEvent } from "@locators/components/shared/hooks/useStoreTrackingEvent";
import { getStoreDetailsPageUrl } from "@locators/components/shared/utils";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import type { FlotiqStore } from "@jti/fe-common";
import { BREAKPOINT_LARGE } from "@jti/fe-common";
import { useViewport } from "@jti/ui/hooks/useViewport";

import { StoreLocatorCard } from "../../StoreLocatorCard";
import { StoreLocatorCardV2 } from "../../StoreLocatorCardV2";

type SingleRowForMapViewProps = {
  activeStoreId: string;
  flotiqStores: FlotiqStore[];
  mapWrapperRef?: RefObject<HTMLDivElement>;
};

export const SingleRowForMapView: FC<SingleRowForMapViewProps> = ({ activeStoreId, flotiqStores, mapWrapperRef }) => {
  const {
    onChooseStore,
    isNoMatchingFlotiqStores,
    storeDetailsPage,
    isOpeningHoursEnabled,
    isOpeningHoursBreaksEnabled,
    isAvailableDevicesEnabled,
    isAvailableSticksEnabled,
    isMultiBrandSticksEnabled,
    isGeneralAnnouncementEnabled,
    isAvailableServicesEnabled,
    isStoreTypeEnabled,
    isVendingMachineEnabled,
    isVendingMachineTipEnabled,
    isNearbyShopsEnabled,
    defaultZoom,
    isAsianAddressFormatEnabled,
    translationPrefix,
  } = useStoreLocatorContext();
  const { __ } = useLocatorTranslations();
  const publishStoreTrackingEvent = useStoreTrackingEvent();
  const store = flotiqStores?.find(({ id }) => id === activeStoreId);
  const { storeLocatorLayoutVersion = "v1", isSeoFriendlyStoreLocatorEnabled } = useConfig();
  const { width } = useViewport();
  const isMobileOrTablet = width < BREAKPOINT_LARGE;
  const storeDetailsPageUrl = getStoreDetailsPageUrl({
    storeDetailsPage,
    store,
    defaultZoom,
    isOpeningHoursEnabled,
    isOpeningHoursBreaksEnabled,
    isAvailableDevicesEnabled,
    isAvailableSticksEnabled,
    isMultiBrandSticksEnabled,
    isGeneralAnnouncementEnabled,
    isAvailableServicesEnabled,
    isStoreTypeEnabled,
    isVendingMachineEnabled,
    isVendingMachineTipEnabled,
    isNearbyShopsEnabled,
    isAsianAddressFormatEnabled,
    isSeoFriendlyUrlsEnabled: isSeoFriendlyStoreLocatorEnabled,
  });

  if (!store) {
    return;
  }

  const resolvedCtaUrl = store?.CtaHref || storeDetailsPageUrl;

  if (storeLocatorLayoutVersion === "v2" && isMobileOrTablet) {
    return (
      <StoreLocatorCardV2
        store={store}
        isListView={false}
        storeDetailsPageUrl={resolvedCtaUrl}
        mapWrapperRef={mapWrapperRef}
        ctaLabel={store?.CtaLabel}
        isAddressLabelVisible={translationPrefix !== "horeca"}
        onSelectCard={publishStoreTrackingEvent}
      />
    );
  }

  return (
    <StoreLocatorCard
      isActive={false}
      store={store}
      isListView={true}
      storeDetailsPageUrl={resolvedCtaUrl}
      ctaLabel={store?.CtaLabel}
    >
      {onChooseStore && (
        <Button variant="primary" onClick={() => onChooseStore(store)} disabled={isNoMatchingFlotiqStores}>
          {__("storelocator.select_store")}
        </Button>
      )}
    </StoreLocatorCard>
  );
};
